export const HOSPITAL_RATING_SCALE = {
  internalMinimum: -10,
  internalMaximum: 10,
  publicMinimum: 1,
  publicMaximum: 5,
} as const

/**
 * 내부 점수(-10~10) → 공개 점수(1~5) 구간별 직선 변환 기준점.
 * 모델의 중립(0점)을 업계 관행상 '보통'인 4.5점으로 보이게 한다.
 * - 0 이하: 4.5 + 점수 × 0.35 (-10 → 1.0)
 * - 0 초과: 4.5 + 점수 × 0.05 (10 → 5.0)
 * 기준점은 내부 점수 오름차순이어야 한다.
 */
export const HOSPITAL_RATING_ANCHORS: ReadonlyArray<readonly [internalScore: number, publicScore: number]> = [
  [-10, 1.0],
  [0, 4.5],
  [10, 5.0],
]

const interpolate = (score: number) => {
  const first = HOSPITAL_RATING_ANCHORS[0]
  const last = HOSPITAL_RATING_ANCHORS[HOSPITAL_RATING_ANCHORS.length - 1]
  // NaN 은 기존 동작처럼 그대로 흘려보낸다 (마지막 기준점 5.0 으로 떨어지지 않게)
  if (Number.isNaN(score)) return score
  if (score <= first[0]) return first[1]
  if (score >= last[0]) return last[1]

  for (let i = 1; i < HOSPITAL_RATING_ANCHORS.length; i++) {
    const [x1, y1] = HOSPITAL_RATING_ANCHORS[i]
    if (score <= x1) {
      const [x0, y0] = HOSPITAL_RATING_ANCHORS[i - 1]
      return y0 + ((y1 - y0) * (score - x0)) / (x1 - x0)
    }
  }
  return last[1]
}

/**
 * 백엔드의 -10~10 리뷰 점수를 사용자와 검색엔진에 공개하는 1~5점 척도로 변환한다.
 * 화면과 JSON-LD가 반드시 이 함수를 함께 사용해야 서로 다른 평점을 노출하지 않는다.
 */
export const toFivePointRating = (score: number) => {
  const { publicMinimum, publicMaximum } = HOSPITAL_RATING_SCALE
  // 환산 연산의 부동소수 오차를 정규화해 4.515 같은 경계값도 올림한다.
  const scaled = Number((interpolate(score) * 100).toPrecision(15))
  const rounded = Math.round(scaled) / 100

  return Math.min(publicMaximum, Math.max(publicMinimum, rounded))
}

export const formatFivePointRating = (score: number) =>
  toFivePointRating(score).toFixed(2)
